import * as React from 'react';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import ReactDOM from 'react-dom';
import { isImage } from '../backoffice/controllers/common';
import { className } from '@empori/util';

export function FullscreenSlide(slide: string) {
  const isSlideImage = isImage(slide);

  return(
    <div className='empori-fullscreen-image-gallery-item'
      style={isSlideImage
        ? { backgroundImage: `url(${slide})`}
        : { backgroundColor: 'black' }}>
      {!isSlideImage &&
        <video controls={true}>
          <source src={slide} type="video/mp4" />
          <source src={slide} type="video/ogg" />
          Your browser does not support the video tag.
        </video>}
    </div>
  );
}

interface ToobarProps {
  onClose: () => void;
  totalSlides: number;
  currentSlideIndex: number;
}

export const FullscreenToolbar = React.forwardRef<HTMLDivElement, ToobarProps>(
  (props, ref) => {
    const [ ready, getReady ] = React.useState<boolean>(false);
    const swiper = useSwiper();
    const prevPossible = props.currentSlideIndex !== 0;
    const nextPossible = props.currentSlideIndex + 1 < props.totalSlides;
  
    // Only for a simple fade-in animation.
    React.useEffect(() => {
      setTimeout(() => {
        getReady(true);
      }, 500);
    }, []);
  
    const handlePrevButtonClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (prevPossible) {
        swiper.slidePrev();
      }
    };
  
    const handleNextButtonClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (nextPossible) {
        swiper.slideNext();
      }
    };
  
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.stopPropagation();
        e.preventDefault();
        if (prevPossible) {
          swiper.slidePrev();
        } else if (nextPossible) {
          swiper.slideNext();
        }
      }
    };
  
    return(
      <div ref={ref} className={className('empori-fullscreen-toolbar element-flex', { 'is-ready': ready })} tabIndex={0}>
        <button
          className={prevPossible ? 'enabled' : 'disabled'}
          disabled={!prevPossible}
          onClick={handlePrevButtonClick}
          onKeyDown={handleKeyDown}
        >
          <i className='fa-regular fa-chevron-left'></i>
        </button>
        <span>
          {`${props.currentSlideIndex + 1}/${props.totalSlides}`}
        </span>
        <button 
          onClick={() => props.onClose()}
          onKeyDown={(e) => {
            if (e.keyCode === 13 || e.key === 'Enter') {
              props.onClose();
            }
          }}>
          <i className='fa-regular fa-times'></i>
        </button>
        <button
          className={nextPossible ? 'enabled' : 'disabled'}
          disabled={!nextPossible}
          onClick={handleNextButtonClick}
          onKeyDown={handleKeyDown}
        >
          <i className='fa-regular fa-chevron-right'></i>
        </button>
      </div>
    );
  }
);

interface ImageGalleryFullscreenProps {
  slideImgUrls: string[];
  toggled: boolean;
  viewingIndex: number;
  open: () => void;
  close: () => void;
  setIndex: (index: number) => void;
}

export function ImageGalleryFullscreen(props: ImageGalleryFullscreenProps) {
  const { toggled, slideImgUrls } = props;
  const toolbarRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (toggled) {
      setTimeout(() => {
        toolbarRef.current?.focus();
      }, 100);
    }
  }, [toggled]);

  if (!toggled || !slideImgUrls) {
    return <div className='empori-image-gallery-fullscreen-trigger'
      onClick={() => props.open()}
      onKeyDown={(e) => {
        if (e.keyCode === 13 || e.key === 'Enter') {
          props.open();
        }
      }}
      tabIndex={0}
    >
      <i className='fa-regular fa-expand'></i>
    </div>;
  }

  return(
    ReactDOM.createPortal(
      <div className='empori-fullscreen-image-gallery visible'>
        <Swiper
          onSlideChange={swiper => props.setIndex(swiper.realIndex)}
          initialSlide={props.viewingIndex}>
          {slideImgUrls.map((slide, index) =>
            <SwiperSlide key={index}>
              {FullscreenSlide(slide)}
            </SwiperSlide>)}
          <FullscreenToolbar
            ref={toolbarRef}
            onClose={props.close}
            totalSlides={slideImgUrls.length}
            currentSlideIndex={props.viewingIndex} />
        </Swiper>
      </div>, document.body
    )
  );
}

interface ImageGalleryFullscreenWrapperProps {
  slideImgUrls: string[];
  onClose: (activeIndex: number) => void;
  activeIndex: number;
}

export function ImageGalleryFullscreenWrapper(props: ImageGalleryFullscreenWrapperProps) {
  const [currentIndex, setCurrentIndex] = React.useState<number>(props.activeIndex);

  return(
    <Swiper
      onSlideChange={fsSwiper => setCurrentIndex(fsSwiper.realIndex)}
      initialSlide={currentIndex}>
      {props.slideImgUrls.map((slide, index) =>
        <SwiperSlide key={index}>
          {FullscreenSlide(slide)}
        </SwiperSlide>)}
      <FullscreenToolbar
        onClose={() => props.onClose(currentIndex)}
        totalSlides={props.slideImgUrls.length}
        currentSlideIndex={currentIndex} />
    </Swiper>
  );
}